/**
 * Hub Registry - Central configuration for all operational hubs
 *
 * This file defines all available hubs in the Willbanks Operations Hub system.
 * Each hub has metadata including routes, permissions, and display information.
 */

import { lazy, ComponentType, LazyExoticComponent } from 'react';
import { FileText, Truck, Settings as SettingsIcon, LucideIcon } from 'lucide-react';
import InveraIcon from '../../hubs/invera-scheduler/components/icons/InveraIcon';
import type { UserRole } from '../../types';

/**
 * Hub icon type - supports both Lucide icons and custom React components
 */
export type HubIcon = LucideIcon | ComponentType<{ className?: string }>;

/**
 * Hub configuration interface
 */
export interface HubConfig {
  key: string;
  name: string;
  description: string;
  icon: HubIcon;
  route: string;
  component: LazyExoticComponent<ComponentType<unknown>> | null;
  enabled: boolean;
  requiresAuth: boolean;
  color: string;
  features: string[];
}

/**
 * Hub definitions
 * Each hub must have:
 * - key: Unique identifier matching database hub_key
 * - name: Display name
 * - description: Brief description for hub selection page
 * - icon: Lucide React icon component
 * - route: Base route path
 * - component: Lazy-loaded React component
 * - enabled: Whether the hub is currently available
 * - requiresAuth: Whether authentication is required
 * - requiredPermissions: Array of permission keys needed for access
 */

const hubs: Record<string, HubConfig> = {
  inveraScheduler: {
    key: 'invera-scheduler',
    name: 'Invera Scheduler',
    description: 'Production scheduling optimization and capacity planning for Invera ERP integration',
    icon: InveraIcon,
    route: '/hub/invera-scheduler',
    component: lazy(() => import('../../hubs/invera-scheduler/index.jsx')),
    enabled: true,
    requiresAuth: true,
    color: 'primary',  // Uses Tailwind primary color
    features: [
      'Intelligent scheduling analysis',
      'Automated schedule generation',
      'Capacity optimization',
      'SOAP integration with Invera'
    ]
  },

  truckRouter: {
    key: 'truck-router',
    name: 'Truck Router',
    description: 'Vehicle routing optimization using OR-Tools for efficient delivery scheduling',
    icon: Truck,
    route: '/hub/truck-router',
    component: lazy(() => import('../../hubs/truck-router/index')),
    enabled: true,
    requiresAuth: true,
    color: 'warning',
    features: [
      'Daily transport order loading',
      'Distance matrix optimization',
      'OR-Tools VRP solver',
      'Route push to INVEX'
    ]
  }
};

/**
 * Get all enabled hubs
 * @returns Array of enabled hub configurations
 */
export const getEnabledHubs = (): HubConfig[] => {
  return Object.values(hubs).filter(hub => hub.enabled);
};

/**
 * Get a specific hub by key
 * @param hubKey - Hub key to retrieve
 * @returns Hub configuration or null if not found
 */
export const getHubByKey = (hubKey: string): HubConfig | null => {
  return Object.values(hubs).find(hub => hub.key === hubKey) || null;
};

/**
 * Get hub route path by key
 * @param hubKey - Hub key
 * @returns Route path or null if not found
 */
export const getHubRoute = (hubKey: string): string | null => {
  const hub = getHubByKey(hubKey);
  return hub ? hub.route : null;
};

/**
 * Check if a hub is enabled
 * @param hubKey - Hub key to check
 * @returns True if hub is enabled
 */
export const isHubEnabled = (hubKey: string): boolean => {
  const hub = getHubByKey(hubKey);
  return hub ? hub.enabled : false;
};

/**
 * Get hubs that user has access to
 * Filter hubs based on user's granted hub access from backend
 * @param userHubKeys - Array of hub keys user has access to
 * @returns Array of hub configurations user can access
 */
export const getUserAccessibleHubs = (userHubKeys: string[] = []): HubConfig[] => {
  return getEnabledHubs().filter(hub =>
    userHubKeys.includes(hub.key)
  );
};

/**
 * Permission mapping for legacy role-based access
 * Maps old roles to hub permissions for backward compatibility
 * This will be deprecated once full hub permission system is implemented
 */
export const LEGACY_ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  admin: ['invera-scheduler', 'truck-router'],
  operator: ['invera-scheduler', 'truck-router'],
  viewer: ['invera-scheduler']
};

/**
 * Get hubs accessible by legacy role
 * @param role - User role (admin, operator, viewer)
 * @returns Array of hub configurations
 */
export const getHubsByLegacyRole = (role: UserRole): HubConfig[] => {
  const allowedHubKeys = LEGACY_ROLE_PERMISSIONS[role] || [];
  return getUserAccessibleHubs(allowedHubKeys);
};

export default hubs;
